import React, { useState, useEffect } from "react";
import { Select, MenuItem, SelectChangeEvent, Box } from "@mui/material";

const FollowUpTypeChip = ({
  initialType,
  onChange,
  disabled = false,
}: {
  initialType: string;
  onChange: (newType: string) => Promise<void> | void;
  disabled?: boolean;
}) => {
  const [followUpType, setFollowUpType] = useState(initialType || "");
  const [isChanging, setIsChanging] = useState(false);

  const typeColors: Record<
    string,
    { bg: string; text: string; border: string }
  > = {
    Whatsapp: { bg: "#e8f5e9", text: "#2e7d32", border: "#2e7d32" },
    Email: { bg: "#e3f2fd", text: "#1565c0", border: "#1565c0" },
    Call: { bg: "#fff3e0", text: "#e65100", border: "#e65100" },
  };

  // Update local type when initialType prop changes
  useEffect(() => {
    setFollowUpType(initialType || "");
  }, [initialType]);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    if (disabled || isChanging) return;

    const newType = event.target.value;
    const previousType = followUpType;

    // Optimistically update the UI
    setFollowUpType(newType);
    setIsChanging(true);

    try {
      // Call the onChange function (which should make the API call)
      await onChange(newType);
    } catch (error) {
      // If API call fails, revert to previous type
      setFollowUpType(previousType);
      console.error("Failed to update follow up type:", error);
    } finally {
      setIsChanging(false);
    }
  };

  const ITEM_WIDTH = 90;

  const currentColors = followUpType
    ? typeColors[followUpType] || { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd" }
    : { bg: "#f5f5f5", text: "#757575", border: "#bdbdbd" };

  return (
    <Select
      value={followUpType || ""}
      onChange={handleChange}
      size="small"
      variant="outlined"
      disabled={disabled || isChanging}
      displayEmpty
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 200,
            padding: "8px",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            border: "1px solid #e0e0e0",
          },
        },
      }}
      sx={{
        width: ITEM_WIDTH,
        fontSize: "0.7rem",
        borderRadius: "16px",
        border: `1px solid ${currentColors.border}`,
        backgroundColor: currentColors.bg,
        color: currentColors.text,
        fontWeight: 600,
        cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          transform: disabled || isChanging ? "none" : "translateY(-1px)",
          boxShadow:
            disabled || isChanging ? "none" : "0 2px 6px rgba(0,0,0,0.1)",
        },
        "&.Mui-disabled": {
          cursor: "not-allowed !important",
          opacity: 0.6,
          backgroundColor: currentColors.bg,
          color: currentColors.text,
          pointerEvents: "auto !important",
        },
        "&.Mui-disabled .MuiSelect-select": {
          cursor: "not-allowed !important",
        },
        "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
          cursor: "not-allowed !important",
          border: `1px solid ${currentColors.border}`,
        },
        "& .MuiSelect-select": {
          padding: "4px 8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        },
        "& .MuiSvgIcon-root": {
          fontSize: "0.9rem",
          color: currentColors.text,
          cursor: disabled || isChanging ? "not-allowed !important" : "pointer",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      }}
    >
      {["Whatsapp", "Email", "Call"].map((option, idx, arr) => (
        <MenuItem
          key={option}
          value={option}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "12px",
            padding: "2px 0px",
            minWidth: ITEM_WIDTH,
            width: ITEM_WIDTH,
            boxSizing: "border-box",
            minHeight: 32,
            marginBottom: idx !== arr.length - 1 ? "8px" : 0,
            backgroundColor: typeColors[option]?.bg,
            color: typeColors[option]?.text,
            fontWeight: 600,
            fontSize: "0.7rem",
            border: `1px solid ${typeColors[option]?.border}`,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              filter: disabled ? "brightness(1)" : "brightness(0.95)",
              transform: disabled ? "scale(1)" : "scale(1.02)",
            },
          }}
        >
          <Box
            sx={{
              padding: 0,
              borderRadius: "8px",
              backgroundColor: "transparent",
              textAlign: "center",
              width: "100%",
              fontWeight: 600,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {option}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
};

export default FollowUpTypeChip;

